import React, {
  useContext,
  useState,
  useCallback,
} from 'react';
import { observer } from 'mobx-react';

import useComponentMounted from '../../../../../../../hooks/useComponentMounted';
import ExternalCoachContext from '../../../../../../context/ExternalCoachContext';
import useToast from '../../../../../../hooks/useToast';
import Select, { MenuItem } from '../../../../../../../components/Select';
import {
  SettingsActionContainer,
  ActionLabel,
  SubTitle,
} from '../styles';
import {
  TitleContainer,
  EmojiOptionContainer,
  EmojiIcon,
} from './styles';
import texts from './texts';

const SKIN_TONE_OPTIONS = [
  { value: 'default', label: texts.skinToneOptions.default, emoji: '👍' },
  { value: 'light', label: texts.skinToneOptions.light, emoji: '👍🏻' },
  { value: 'mediumLight', label: texts.skinToneOptions.mediumLight, emoji: '👍🏼' },
  { value: 'medium', label: texts.skinToneOptions.medium, emoji: '👍🏽' },
  { value: 'mediumDark', label: texts.skinToneOptions.mediumDark, emoji: '👍🏾' },
  { value: 'dark', label: texts.skinToneOptions.dark, emoji: '👍🏿' },
];

const EmojiSkinToneSelector = () => {
  const {
    coachDoc,
    coachDoc: {
      emojisSkinTone,
    },
  } = useContext(ExternalCoachContext);

  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  const [selectedSkinTone, setSelectedSkinTone] = useState(emojisSkinTone || 'default');

  const handleSkinToneChange = useCallback(async (event) => {
    const newSkinTone = event.target.value;
    try {
      setSelectedSkinTone(newSkinTone);
      await coachDoc.setEmojisSkinTone(newSkinTone);

      if (isComponentMountedRef.current) {
        showToast(texts.emojiSkinToneSaveSuccessful);
      }
    } catch (error) {
      showToast(texts.emojiSkinToneSaveFailed, { error: true });
      // Revert on error
      setSelectedSkinTone(emojisSkinTone || 'default');
    }
  }, [
    coachDoc,
    isComponentMountedRef,
    showToast,
    emojisSkinTone,
  ]);

  const renderSkinToneOptions = useCallback((options) => (
    options.map(({ value, label, emoji }) => (
      <MenuItem key={value} value={value}>
        <EmojiOptionContainer>
          <EmojiIcon>{emoji}</EmojiIcon>
          <span>{label}</span>
        </EmojiOptionContainer>
      </MenuItem>
    ))
  ), []);

  return (
    <SettingsActionContainer>
      <TitleContainer>
        <ActionLabel>{texts.emojiSkinTone}</ActionLabel>
        <SubTitle>{texts.emojiSkinToneDescription}</SubTitle>
      </TitleContainer>
      <Select
        value={selectedSkinTone}
        onChange={handleSkinToneChange}
        options={SKIN_TONE_OPTIONS}
        optionParserFunction={renderSkinToneOptions}
      />
    </SettingsActionContainer>
  );
};

export default observer(EmojiSkinToneSelector);
